<template>
  <div id="app">
    <router-view/>
    <Modal
      :mask="false"
      :value="$store.getters['HelpCenter/visibility']"
      :title="$t('help_center.title')"
      :footer-hide="$store.getters['HelpCenter/footer']"
      @on-visible-change="$store.dispatch('HelpCenter/toggle')"
      :draggable="true"
      :scrollable="true">
      <component :is="$store.getters['HelpCenter/component']"></component>
    </Modal>
    <MonitorDrawer :visible="monitor" @close="monitor = false" />
  </div>
</template>

<script>
import Vue from 'vue';
import { Modal } from 'iview';
import Sentry from '@/plugins/sentry';
import Pendo from '@/plugins/pendo';
import ModelHelp from '@/components/HelpCenter/modal.vue';
import MonitorDrawer from '@/components/MonitorDrawer.vue';

Vue.component('Modal', Modal);
Vue.component('ModelHelp', ModelHelp);
Vue.component('MonitorDrawer', MonitorDrawer);

export default {
  data: () => ({
    buttonModalProps: {
      style: {
        display: 'none',
      },
    },
    monitor: false,
  }),
  mounted() {
    document.addEventListener('monitor', event => (this.monitor = event.detail.open));
    this.setLang();
    const redirect = () => !(['/login', '/accounts/activation', '/password/forgot'].includes(window.location.pathname)) && !window.location.pathname.includes('/login/claim');

    this.$authorization.on('expired', (token) => {
      if (token === 'refreshToken') this.$authorization.logout();
      if (redirect() && token === 'refreshToken') this.$router.push({ name: 'login', query: { redirect: this.$urls.fullPathName() } });
    });

    this.$authorization.on('empty', () => (redirect()) && this.$router.push({ name: 'login', query: { redirect: this.$urls.fullPathName() } }));

    this.$authorization.on('error', (error) => {
      if (redirect()) this.$router.push({ name: 'login', query: { redirect: this.$urls.fullPathName() } });
      this.$authorization.logout();
      Sentry.withScope((scope) => {
        scope.setTag('@videsk/jwt-webauth', this.$authorization.version);
        scope.setLevel('error');
        Sentry.captureException(error);
      });
    });

    this.$authorization.on('ready', async () => {
      this.setAuthorizationHeader();
      await this.wait(1000);
      try {
        await this.getUserMetadata();
        this.setPendo();
        this.feedbackChangelog();
      } catch (e) {
        console.error(e);
      } finally {
        this.$store.dispatch('live-monitor/connect');
      }
    });
    this.$authorization.set();

    this.axios.interceptors.response.use(null, async (error) => {
      if (error.config && error.response && error.response.status === 401 && !error.config.url.includes('/auth/')) return this.retryInterceptorSetup(error);
      if (error.config && error.response && error.response.status === 429) {
        await this.wait(1000);
        return this.retryInterceptorSetup(error);
      }
      return Promise.reject(error);
    });

    if (Object.is) return;

    Object.is = (x, y) => {
      if (x === y) return x !== 0 || 1 / x === 1 / y;
      // eslint-disable-next-line no-self-compare
      return x !== x && y !== y;
    };
  },
  methods: {
    setLang() {
      const lang = (window.navigator.language) && window.navigator.language.split('-')[0];
      document.querySelector('html').setAttribute('lang', lang || 'en');
    },
    setAuthorizationHeader() {
      const accessToken = this.$authorization.getTokens('accessToken');
      this.axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    },
    async getUserMetadata() {
      const decode = JWT => JSON.parse(atob(JWT.split('.')[1]));
      const accessToken = this.$authorization.getTokens('accessToken');
      const { aud, org } = decode(accessToken);
      this.$store.commit('global/account', org);
      this.$store.commit('global/id', aud);
      await Promise.all([this.$store.dispatch('global/getUserData'), this.$store.dispatch('global/loadFeatures')]);
      const data = this.$store.getters['global/features'] || {};
      const features = data.features || [];
      if (window.profitwell) window.profitwell('start', { user_id: aud });
      this.$router.options.routes[0].children.forEach((menu, index) => {
        const featureName = menu.feature;
        const exist = features.some(feature => feature.name === featureName);
        if (exist) this.$router.options.routes[0].children[index].menu = true;
      });
    },
    async retryInterceptorSetup(error) {
      try {
        await this.$authorization.renew();
        const accessToken = this.$authorization.getTokens('accessToken');
        // eslint-disable-next-line no-param-reassign
        error.config.headers.Authorization = `Bearer ${accessToken}`;
        return this.axios.request(error.config);
      } catch (catchError) {
        return Promise.reject(catchError);
      }
    },
    wait(timeout = 1000) {
      return new Promise(resolve => setTimeout(resolve, timeout));
    },
    setPendo() {
      Pendo(
        {
          firstname: this.$store.getters['global/user'].firstname,
          lastname: this.$store.getters['global/user'].lastname,
          email: this.$store.getters['global/user'].email,
          id: this.$store.getters['global/id'],
        },
        {
          // eslint-disable-next-line no-underscore-dangle
          id: this.$store.getters['global/accountObj']._id,
          name: this.$store.getters['global/accountObj'].name,
        },
      );
    },
    async feedbackChangelog() {
      const ssoToken = await this.$ssoToken('feedback', this.$authorization.getTokens('accessToken'));
      window.feedbackContainer = await window.Frill('container', {
        key: 'e148447a-c2a0-4c0a-8ec6-3109b1e9df75',
        ssoToken,
        autoLoad: 'surveys',
      });
      window.Frill('identify', { ssoToken });
      window.feedbackWidget = await window.Frill('widget', { key: '789d7d98-98b6-447c-8803-a6df1c22e22c' });
    },
  },
};
</script>

<style>
body {
  background: #f5f6f8;
}

#app {
  width: 100%;
  height: 100%;
}

.vd__help_modal [class*="footer"] {
  display: none;
}
</style>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

* {
  font-family: 'Inter', sans-serif;
}
</style>

<style>
@media screen and (max-width: 800px) {
  .vd__box_body {
    overflow-x: auto !important;
  }
  .vd__header {
    height: auto !important;
    padding: 2rem 0 !important;
  }
  .vd__menu_nav {
    height: auto !important;
    flex-direction: column;
    align-items: flex-end !important;
  }
  .vd__menu_logo, .vd__login_promo_container {
    display: none;
  }
  .vd__menu_items, .vd__menu_account {
    height: auto !important;
  }
  .vd__menu_items {
    flex-wrap: wrap;
  }
  .vd__insights_menu {
    flex-direction: column;
    padding: 1rem;
  }
  .vd__insights_menu ul li, .vd__menu_item {
    margin-bottom: 1rem;
  }
  .filters-container {
    flex-direction: column;
    align-items: flex-start !important;
  }
  .filters-container > *, .vd__box_header_actions_buttons > *, .vd__ssl_form > * {
    margin: 0 0 10px 0 !important;
  }
  .notification-exports > *:last-child {
    margin-top: 1rem;
  }
  .vd__box_header_actions_buttons {
    flex-direction: column;
    align-items: flex-end !important;
  }
  .forms-builder-container .container .sidebar{
    width: 19rem;
  }
  .forms-builder-container .container .content-form {
    width: calc(100% - 19rem);
  }
  .notification-exports, .form-element .control-group, .form-element .control-group .preview-title, .form-element .control-group .control-group, .vd__ssl_form, .vd__account-top-container {
    flex-direction: column;
  }

  .form-element .control-group .preview-title, .form-element .control-group .control-group {
    padding: 10px 0;
  }

  .form-element .control-group .control-group .button-control {
    margin-bottom: 10px;
  }
  .vd__ssl_form {
    align-items: flex-start !important;
  }
  .vd__ssl_form > *, .vd__account-page-body {
    width: 100% !important;
    max-width: 100% !important;
  }
  .vd__account-sidebar {
    width: 100% !important;
    flex-direction: row !important;
  }
  .vd__account-item > a {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .vd__login_form_container {
    width: auto !important;
  }
}

iframe#canny-changelog-iframe {
  top: unset !important;
  position: fixed !important;
  left: unset !important;
  bottom: 0 !important;
  right: 1rem !important;
}

iframe#jsd-widget {
    right: unset;
    left: 10px;
}
</style>
